import { useMemo } from 'react';
import { useDecision } from '@optimizely/react-sdk';

import { FeatureFlags } from '@frontend/constants';

import { useOperations } from './useOperations';
import { useToolPermissions } from './useToolPermissions';

export type ConnectorTabVisibility = {
  canViewAuthConnections: boolean;
  canViewTools: boolean;
  canViewRegistry: boolean;
  canViewExternalProviders: boolean;
  canViewMCPServers: boolean;
};

/**
 * Visibility of each Connectors sub-tab, mirroring exactly the gates ToolsManagement
 * uses to render its tab bar. Extracted so other surfaces (e.g. the Spotlight palette)
 * can offer the same sub-tabs without drifting from what the page actually shows.
 *
 * Flag reads go through `useDecision`/`useToolPermissions` rather than the deprecated
 * `useFeature`; `canManageExternalProviders` already reflects `tools_auth_manager` so we
 * don't read that flag twice.
 *
 * The MCP-servers tab is region-agnostic here — an off-US instance still sees the tab,
 * it just renders a "not available in this region" page. Region is not part of tab visibility.
 */
export function useConnectorTabVisibility(): ConnectorTabVisibility {
  const { canUser } = useOperations();
  const {
    canManageOcpTools,
    canManageThirdpartyTools,
    canManageSystemTools,
    canManageExternalProviders,
  } = useToolPermissions();
  const remoteMcpEnabled = useDecision(FeatureFlags.REMOTE_MCP_SERVERS)[0].enabled;
  const allAdminsViewProviders = useDecision(FeatureFlags.EXTERNAL_PROVIDERS_ALL_ADMINS)[0].enabled;

  return useMemo(
    () => ({
      // Connectors (auth connections) always live on this page, not the Directory.
      canViewAuthConnections: true,
      canViewTools: canUser(['view_tools']),
      canViewRegistry: canManageOcpTools || canManageThirdpartyTools,
      // External Providers tab: system admins always, instance admins only when feature-flagged.
      canViewExternalProviders:
        canUser(['manage_thirdparty_tools']) &&
        (allAdminsViewProviders || canManageExternalProviders || canManageSystemTools),
      canViewMCPServers: remoteMcpEnabled && canManageSystemTools,
    }),
    [
      canUser,
      canManageOcpTools,
      canManageThirdpartyTools,
      canManageSystemTools,
      canManageExternalProviders,
      remoteMcpEnabled,
      allAdminsViewProviders,
    ]
  );
}
